import { useParams, Link } from "react-router-dom";
import { useFetch } from "@/lib/useFetch";
import { Seo } from "@/lib/seo";
import { Loading } from "@/components/States";
import { DetailError } from "@/components/DetailError";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { VenueCard, EventCard } from "@/components/Cards";
import { ArrowUpRight } from "lucide-react";

export default function LeaguePage() {
  const { league } = useParams();
  const { data, loading, error } = useFetch(`/league/${league}`, [league]);

  if (loading) return <Loading />;
  if (error || !data?.league) return <DetailError error={error} path={`/${league}`} notFound={!error} />;
  const { league: lg, teams, venues, events } = data;

  return (
    <div className="mx-auto max-w-7xl px-5 sm:px-8 py-12">
      <Seo title={`${lg.name} GameDay — Teams, Venues & Where to Stay | Hotelstrips`}
        description={`Plan travel around ${lg.name} games. Browse teams, venues and upcoming events, with the best areas to stay for each.`}
        path={`/${lg.slug}`} />
      <Breadcrumbs items={[{ label: "GameDay", to: "/gameday" }, { label: lg.name }]} />

      <div className="mb-10">
        <div className="overline text-primary mb-2">{lg.name}</div>
        <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter text-ink">{lg.name} travel</h1>
        <p className="text-muted-foreground mt-3 max-w-2xl">{lg.description}</p>
      </div>

      {teams?.length > 0 && (
        <div className="mb-12">
          <h2 className="font-heading text-2xl font-extrabold tracking-tight text-ink mb-5">Teams</h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {teams.map((t) => (
              <Link key={t.slug} to={`/${lg.slug}/${t.slug}`} data-testid={`team-${t.slug}`}
                className="group flex items-center justify-between rounded-xl bg-card border border-black/5 card-shadow p-5 transition-transform duration-300 hover:-translate-y-1">
                <div>
                  <span className="inline-block h-2.5 w-2.5 rounded-full mr-2 align-middle" style={{ background: t.primary_color }} />
                  <span className="font-heading text-lg font-bold text-ink align-middle">{t.name}</span>
                  <div className="text-xs text-muted-foreground mt-1 ml-4">{t.venue_slug?.replace(/-/g, " ")}</div>
                </div>
                <ArrowUpRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
              </Link>
            ))}
          </div>
        </div>
      )}

      {venues?.length > 0 && (
        <div className="mb-12">
          <h2 className="font-heading text-2xl font-extrabold tracking-tight text-ink mb-5">Venues</h2>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {venues.map((v) => <VenueCard key={v.slug} venue={v} />)}
          </div>
        </div>
      )}

      {events?.length > 0 && (
        <div>
          <h2 className="font-heading text-2xl font-extrabold tracking-tight text-ink mb-5">Upcoming events</h2>
          <div className="grid gap-4 md:grid-cols-2">
            {events.map((e) => <EventCard key={e.slug} event={e} />)}
          </div>
        </div>
      )}
    </div>
  );
}
